import {
  Create,
  getValueFromEvent,
  useFileUploadState,
  useForm,
  useStepsForm,
  useSelect,
} from "@refinedev/antd";
import { InboxOutlined, UnorderedListOutlined, CloudServerOutlined, FileOutlined, DatabaseOutlined } from "@ant-design/icons";
import { Button, Form, Select, Steps, Tag, Upload, Result, theme, Space } from "antd";
import { IResourceComponentsProps, useApiUrl, useNotification, useNavigation } from "@refinedev/core";

import { IVesselDataTransfer, IVessel } from "interfaces";
import { FormSection, FormFieldGrid } from "components/form";
import React from "react";

export const VesselDataTransferCreate: React.FC<
  IResourceComponentsProps
> = () => {
  const [isRefreshed, setIsRefreshed] = React.useState(false);
  const [isFileUploaded, setIsFileUploaded] = React.useState(false);
  const [refreshList, setRefreshList] = React.useState(false);
  const { list } = useNavigation();
  const { token } = theme.useToken();

  const {
    current,
    gotoStep,
    stepsProps,
    formProps,
    formLoading: isLoading,
  } = useStepsForm<IVesselDataTransfer>({
    resource: "vessel-data-transfer",
    redirect: "list"
  });

  const { onChange, isLoading: isFileUploading } = useFileUploadState();
  const apiUrl = useApiUrl();
  const { open } = useNotification();

  function onFileUpload(info: any) {
    onChange(info);

    const { status, name, response } = info.file;

    if (status === 'uploading') {
      setIsRefreshed(false);
    }

    if (status === 'done') {
      setRefreshList(prev => !prev);
      setIsRefreshed(true);
      open?.({
        type: "success",
        message: `File ${name} uploaded successfully`,
        description: "Upload Successful"
      });
    } else if (status === 'error') {
      open?.({
        type: "error",
        message: response?.message || ` ${name} failed to upload. Please try again.`,
        description: "Upload Failed"
      });
    }

    setIsFileUploaded(true);
  }

  const { selectProps: vesselSelectProps } = useSelect<IVessel>({
    resource: "vessel",
    optionLabel: "name",
    optionValue: "imoNumber",

    pagination: {
      mode: "server"
    }
  });

  const props = {
    name: "file",
    multiple: false,
    maxCount: 1,
    action: `${apiUrl}/upload/vessel-data-transfer/${formProps.form?.getFieldValue(['vessel', 'imoNumber']) || ''}/${formProps.form?.getFieldValue('requestType') || ''}`,
    onChange: onFileUpload,
    onRemove: () => {
      setIsRefreshed(false);
      setIsFileUploaded(false);
    },
  };

  const formList = [
    <FormSection
      title="Vessel Selection"
      subtitle="Select vessel for data transfer"
      icon={<CloudServerOutlined />}
      variant="elevated"
    >
      <FormFieldGrid columns={1}>
        <Form.Item
          label="Vessel"
          name={["vessel", "imoNumber"]}
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Select {...vesselSelectProps} />
        </Form.Item>
      </FormFieldGrid>
    </FormSection>,
    <FormSection
      title="Request Type"
      subtitle="Select data transfer type"
      icon={<FileOutlined />}
      variant="elevated"
    >
      <FormFieldGrid columns={1}>
        <Form.Item
          label="Request Type"
          name="requestType"
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Select
            placeholder="Select request type"
            options={[
              { label: "AVCS Exchange Set", value: "AVCS Exchange Set" },
              { label: "AVCS Permit", value: "AVCS Permit" },
              { label: "AIO Update", value: "AIO Update" },
              { label: "ENP Base Data", value: "ENP Base Data" },
              { label: "Misc Files", value: "Misc Files" },
            ]}
          />
        </Form.Item>
      </FormFieldGrid>
    </FormSection>,
    <FormSection
      title="File Upload"
      subtitle="Upload requested data file (max 30MB)"
      icon={<DatabaseOutlined />}
      variant="elevated"
    >
      <Form.Item
        name="file"
        valuePropName="file"
        getValueFromEvent={getValueFromEvent}
      >
        <Upload.Dragger {...props} listType="picture">
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Click or drag & drop file to this area to upload
          </p>
          <p className="ant-upload-hint">
            Support for a single upload of requested data file
          </p>
          <p className="ant-upload-hint" style={{ color: token.colorError }}>
            Maximum file size: 30MB
          </p>
        </Upload.Dragger>
        {isRefreshed ? (
          <Result
            status="success"
            title="Data Uploaded Successfully"
            subTitle="Kindly go to the list page for the updated data."
          />
        ) : (
          <></>
        )}
      </Form.Item>
    </FormSection>,
  ];

  return (
    <Create
      breadcrumb={false}
      headerProps={{
        onBack: () => list("vessel-data-transfer"),
        title: "Vessel Data Transfer",
        subTitle: "data requests from vessels",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
        extra: (<></>),
      }}
      saveButtonProps={{
        children: "Go to List",
        icon: <UnorderedListOutlined />,
        onClick: () => list("vessel-data-transfer"),
        style: { display: isFileUploaded ? 'block' : 'none' }
      }}
    >
      <Steps {...stepsProps}>
        <Steps.Step title="Select Vessel" />
        <Steps.Step title="Select Request Type" />
        <Steps.Step title="Upload Request File" />
      </Steps>
      <div>
        <Form {...formProps} layout="vertical" style={{ paddingTop: 20 }}>
          <Space direction="vertical" size={24} style={{ width: "100%" }}>
            {formList[current]}
          </Space>
        </Form>
        <>
          {current > 0 && (
            <div
              style={{
                paddingTop: 20,
              }}
            >
              <Button
                onClick={() => {
                  gotoStep(current - 1);
                }}
                disabled={isFileUploading}
              >
                Previous
              </Button>
            </div>
          )}
          {current < formList.length - 1 && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button
                onClick={() => {
                  gotoStep(current + 1);
                }}
              >
                Next
              </Button>
            </div>
          )}
        </>
      </div>
    </Create>
  );
};
